import { type MouseEvent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Flag, Header, Icon, UserMenu } from '@opswat/ui';
import { useTheme } from 'hooks/useTheme';
import { useLanguage } from 'hooks/useLanguage';
import { useQueryFilter } from 'hooks/useQueryFilter';
import { signIn, signOut } from 'services/authenticator';
import { useUser } from 'services/User';
import { navigate } from 'gatsby';

interface PublicLayoutProps {
    children: ReactNode;
}

export const PublicLayout = ({ children }: PublicLayoutProps) => {
    const { t } = useTranslation();
    const { user } = useUser();
    const { clearGlobal } = useQueryFilter();
    const { theme, themeOptions, currentTheme, setTheme } = useTheme();
    const { languages, currentLanguage, changeLanguage } = useLanguage();
    const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || '';

    const [visibleMenu, setVisibleMenu] = useState<'default' | 'theme' | 'language'>('default');

    const handleBackToDefault = (e: MouseEvent) => {
        e.preventDefault();
        setVisibleMenu('default');
    };

    const handleLogout = () => {
        clearGlobal();
        signOut();
    };

    const BackToDefaultMenuItem = () => (
        <>
            <UserMenu.Item onClick={handleBackToDefault} className='tw:text-neutrals-800 tw:dark:text-neutrals-500'>
                <Icon iconName='input-left' className='tw:bg-neutrals-800 tw:dark:bg-neutrals-500' />
                {t('Back')}
            </UserMenu.Item>
            <UserMenu.Divider />
        </>
    );

    const userMenu = (
        <UserMenu onOpenChange={() => setVisibleMenu('default')}>
            <UserMenu.Trigger onClick={() => setVisibleMenu('default')}>
                <Avatar name={fullName} />
            </UserMenu.Trigger>
            <UserMenu.Dropdown className='tw:w-236'>
                {visibleMenu === 'default' ? (
                    <>
                        {fullName && <>
                            <UserMenu.Item>
                                <div className='tw:flex tw:flex-col'>
                                    <div className='tw:text-headline tw:font-medium'>{fullName}</div>
                                    <div className='tw:text-body tw:text-neutrals-700 tw:dark:text-neutrals-500 tw:break-all'>{user?.email ?? ''}</div>
                                </div>
                            </UserMenu.Item>
                            <UserMenu.Divider />
                        </>}
                        <UserMenu.Item onClick={() => navigate('/licensing')}>
                            <div className='tw:flex tw:items-center tw:gap-8'>
                                <Icon iconName='list-bullet' />
                                <span>{t('Licensing')}</span>
                            </div>
                        </UserMenu.Item>
                        <UserMenu.Divider />
                        <UserMenu.Item onClick={(e: MouseEvent) => { e.preventDefault(); setVisibleMenu('theme'); }}>
                            <div className='tw:flex tw:items-center tw:gap-8 tw:justify-between tw:w-full'>
                                <div className='tw:flex tw:items-center tw:gap-8'>
                                    <Icon iconName={currentTheme.icon} />
                                    <span>{currentTheme.label}</span>
                                </div>
                                <Icon iconName='input-right' />
                            </div>
                        </UserMenu.Item>
                        <UserMenu.Item onClick={(e: MouseEvent) => { e.preventDefault(); setVisibleMenu('language'); }}>
                            <div className='tw:flex tw:items-center tw:gap-8 tw:justify-between tw:w-full'>
                                <div className='tw:flex tw:items-center tw:gap-8'>
                                    <Flag countryCode={currentLanguage?.flag ?? 'US'} />
                                    <span>{currentLanguage?.name ?? t('English')}</span>
                                </div>
                                <Icon iconName='input-right' />
                            </div>
                        </UserMenu.Item>
                        <UserMenu.Divider />
                        {user?.isLoggedIn ? (
                            <UserMenu.Item onClick={handleLogout}>
                                <div className='tw:flex tw:items-center tw:gap-8'>
                                    <Icon iconName='logout' />
                                    <span>{t('Logout')}</span>
                                </div>
                            </UserMenu.Item>
                        ) : (
                            <UserMenu.Item onClick={signIn}>
                                <div className='tw:flex tw:items-center tw:gap-8'>
                                    <Icon iconName='login' />
                                    <span>{t('Login')}</span>
                                </div>
                            </UserMenu.Item>
                        )}
                    </>
                ) : null}

                {visibleMenu === 'theme' ? (
                    <>
                        <BackToDefaultMenuItem />
                        {themeOptions.map(({ label, icon, value }) => (
                            <UserMenu.Item key={label} onClick={() => setTheme(value)}>
                                <div className='tw:flex tw:items-center tw:gap-8'>
                                    <Icon iconName={icon} />
                                    <span className='tw:flex-1'>{label}</span>
                                    {theme === value && <Icon iconName='check' />}
                                </div>
                            </UserMenu.Item>
                        ))}
                    </>
                ) : null}

                {visibleMenu === 'language' ? (
                    <>
                        <BackToDefaultMenuItem />
                        {languages.map(({ code, name, flag, isSelected }) => (
                            <UserMenu.Item key={code} onClick={() => changeLanguage(code)}>
                                <div className='tw:flex tw:items-center tw:gap-8'>
                                    <Flag countryCode={flag ?? 'US'} />
                                    <span className='tw:flex-1'>{name}</span>
                                    {isSelected && <Icon iconName='check' />}
                                </div>
                            </UserMenu.Item>
                        ))}
                    </>
                ) : null}
            </UserMenu.Dropdown>
        </UserMenu>
    );

    return (
        <div className='tw:w-screen tw:h-screen tw:flex tw:flex-col tw:justify-between'>
            <header
                className='tw:flex tw:flex-row tw:justify-between tw:items-start tw:w-full tw:p-20 tw:border-b tw:border-neutrals-200 tw:dark:border-neutrals-1000'
                role='banner'
            >
                <Header.Product productSubtitle='Cloud' />
                {userMenu}
            </header>
            <main className='tw:w-full tw:h-full tw:p-20 tw:overflow-y-auto'>{children}</main>
        </div>
    );
};
